(ns manage.content-configuration-page.context-source.db
  (:require [clojure.set :refer [subset?]]
            [common.ui.add-item :refer [generate-unique-id]]
            [common.ui.validation.db :refer [transform-entity-validate]]
            [datascript.core :as d]
            [lemonpi.content-function.shared.validation :as v]
            [manage.content-configuration-page.context-source.automatic-split-testing]
            [manage.content-configuration-page.context-source.macro]
            [manage.content-configuration-page.context-source.schema :refer [selector->schema]]
            [manage.content-configuration-page.context-source.shared :as cs]
            [re-frame.core :as rf]
            [re-posh.core :as rp]))

(rf/reg-event-fx ::create
  [(rf/inject-cofx :sub :resource.schema/schemas)
   (rf/inject-cofx :sub :router/query-parameters-pre-coercion)]
  (fn [{:keys [:resource.schema/schemas]
        :router/keys [query-parameters-pre-coercion]} [_ schema-id]]
    (let [alias (generate-unique-id :context-source/alias
                                    (some #(when (= schema-id (:id %))
                                             (:alias %))
                                          schemas))
          param (cs/parameter {:context-source/schema schema-id})]
      (when (v/valid? :alias/name alias)
        {:fx [[:dispatch [:resource.schema/get-schema-output-structure schema-id param]]
              [:dispatch [:resource.schema/get-schema-output-data schema-id param]]
              [:dispatch [:resource.schema/get-parameter-schema schema-id]]
              [:transact [{:context-source/schema schema-id
                           :context-source/alias alias
                           :content-configuration/_context-source [:content-configuration/id "default"]}]]

              ;; When the created context source requires additional configuration, the user is asked
              ;; if he wants to do this configuration now.
              (let [entity-query-params (cs/entity-query-params {:schema-id schema-id :alias alias})
                    already-there? (subset? (set (update-vals entity-query-params name))
                                            (set query-parameters-pre-coercion))]
                (when-not already-there?
                  [:dispatch [:modal/open {:id :configure-context-source?
                                           :props {:on-submit #(rf/dispatch [:entity/view entity-query-params])}}]]))]}))))

(rp/reg-pull-sub ::db-context-sources
  [{:content-configuration/context-source cs/pull-pattern-context-source}])

(rf/reg-sub ::context-sources
  :<- [::db-context-sources [:content-configuration/id "default"]]
  (fn [db-context-sources _]
    (:content-configuration/context-source db-context-sources)))

(rf/reg-sub ::used-schemas-ids
  :<- [::context-sources]
  (fn [context-sources _]
    (map :context-source/schema context-sources)))

(rf/reg-event-fx ::load-param-schemas
  [(rf/inject-cofx :sub [::context-sources])]
  (fn [{::keys [context-sources]} _]
    {:fx (for [schema-id (distinct (map :context-source/schema context-sources))]
           [:dispatch [:resource.schema/get-parameter-schema schema-id]])}))

(rf/reg-event-fx ::load-schema-output-structures
  [(rf/inject-cofx :sub [::context-sources])]
  (fn [{::keys [context-sources]} _]
    {:fx (for [context-source context-sources]
           [:dispatch [:resource.schema/get-schema-output-structure (:context-source/schema context-source) (cs/parameter context-source)]])}))

(rf/reg-event-fx ::load-schema-output-data
  [(rf/inject-cofx :sub [::context-sources])]
  (fn [{::keys [context-sources]} _]
    {:fx (for [context-source context-sources]
           [:dispatch [:resource.schema/get-schema-output-data (:context-source/schema context-source) (cs/parameter context-source)]])}))

(rf/reg-sub :context-source/selector-schemas
  (fn [[_ {:context-source/keys [schema] :as context-source} _]]
    (rf/subscribe [:resource.schema/output schema (cs/parameter context-source)]))
  (fn [selector-schemas [_ _ selector]]
    (cond->> selector-schemas
      (some? selector)
      (selector->schema selector))))

(rf/reg-event-fx ::delete
  [(rf/inject-cofx :ds)]
  (fn [{:keys [ds]} [_ {cs :eid}]]
    (let [used-in-rules
          (not-empty (d/q '[:find [?n ...]
                            :in $ ?cs
                            :where
                            [?cs :context-source/macro ?m]
                            (or-join [?m ?r]
                                     (and
                                      [?sphe :singular-placeholder-expression/macro ?m]
                                      [?r :rule/assignment ?sphe])
                                     [?r :rule/condition ?m])
                            [?r :rule/name ?n]]
                          ds cs))
          used-in-feed-lookups
          (not-empty (d/q '[:find [?a ...]
                            :in $ ?cs
                            :where
                            [?cs :context-source/macro ?m]
                            [?l :feed-context-source-lookup/macro ?m]
                            [?fcs :feed-context-source/lookup ?l]
                            [?fcs :context-source/alias ?a]]
                          ds cs))
          used-in-placeholders
          (not-empty (d/q '[:find ?p ?n
                            :in $ ?cs
                            :where
                            [?cs :context-source/macro ?m]
                            (or-join [?p ?m]
                                     (and [?r :rule/condition ?m]
                                          [?tv :mapped-targeting-value/rule ?r]
                                          [?tk :targeting-key/targeting-value ?tv]
                                          [?tk :targeting-key/placeholder ?p])
                                     (and [?p :placeholder/override ?phe]
                                          [?phe :singular-placeholder-expression/macro ?m]))
                            [?p :placeholder/name ?n]]
                          ds cs))]
      (if (or used-in-rules used-in-feed-lookups used-in-placeholders)
        {:fx [[:dispatch [:modal/open {:id :manage.content-configuration-page.context-source.view/confirm-delete
                                       :props {:rule-names used-in-rules
                                               :feed-context-source-aliases used-in-feed-lookups
                                               :placeholders+names used-in-placeholders
                                               :cs cs}}]]]}
        {:transact [[:db/retractEntity cs]]}))))

(rf/reg-event-fx ::parameter-change-update-schema-output-structure
  [(rf/inject-cofx :sub ::context-sources)
   (rf/inject-cofx :ds)
   (rf/inject-cofx :sub :resource.schema/output-structures)]
  (fn [{::keys [context-sources] output-structures :resource.schema/output-structures db :ds} [_ tx-data]]
    (let [changed-feed-context-source-lookups (comp (filter #(contains? #{:feed-context-source-lookup/id
                                                                          :feed-context-source-lookup/macro} (:a %)))
                                                    (map :e)
                                                    (map (partial d/datoms db :avet :feed-context-source/lookup))
                                                    (map first))
          changed-dsp-signal-context-source-signals (comp (filter #(contains? #{:dsp-signal/name
                                                                                :dsp-signal/dsp-macro} (:a %)))
                                                          (map :e)
                                                          (map (partial d/datoms db :avet :dsp-signal-context-source/signal))
                                                          (map first))
          changed-context-sources (filter #(contains? #{:products-context-source/product-selector-id
                                                        :manual-split-testing-context-source/split
                                                        :feed-context-source/feed-id
                                                        :feed-context-source/lookup
                                                        :dsp-signal-context-source/signal
                                                        :custom-context-source/selector
                                                        :custom-schema-selector/name
                                                        :poi-context-source/selector-id
                                                        :poi-context-source/max-radius-km} (:a %)))

          changed-context-source-eids
          (not-empty (reduce (fn [res f] (into res (comp f (map :e)) tx-data)) #{}
                             [changed-feed-context-source-lookups
                              changed-dsp-signal-context-source-signals
                              changed-context-sources]))]
      ;; Known issue: there might be a small 'memory leak' if the parameter of a
      ;; context source changes very often, because the database with output
      ;; structures is not cleaned up. In practice this should not become a very
      ;; big issue, but in case memory usage does grow significantly, a different
      ;; approach should be taken, which can complicate things. In the end it's
      ;; all about cost vs benefit. If the parameter is changed to a previously
      ;; used value, no call will be made to the backend, because the value is
      ;; cached.
      {:fx (apply concat
                  (for [cs changed-context-source-eids
                        :let [context-source (some #(when (= (:db/id %) cs) %) context-sources)
                              schema-id (:context-source/schema context-source)
                              param (cs/parameter context-source)]
                        :when (and (some? schema-id) (nil? (get-in output-structures [schema-id param])))]
                    (list [:dispatch [:resource.schema/get-schema-output-structure schema-id param]]
                          [:dispatch [:resource.schema/get-schema-output-data schema-id param]])))})))

(rf/reg-sub ::schema-api-schema
  (fn [[_ cs]]
    (rf/subscribe [:resource.schema/schema-id->meta+data (:context-source/schema cs) (cs/parameter cs)]))
  (fn [schema _]
    schema))

(defmethod transform-entity-validate :macro
  [_ m]
  (cond-> m
    (vector? (:context-source/_macro m))
    (update :context-source/_macro (partial apply hash-map))))

(defmethod transform-entity-validate :context-source
  [_ cs]
  (let [cs (dissoc cs :context-source/macro :context-source/original-parameter)
        schema @(rf/subscribe [::schema-api-schema cs])]
    (cond-> cs
      (some? schema)
      (assoc :context-source/schema schema))))
